import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { 
  HeartPulse, ArrowLeft, Search, Mic, X, 
  FlaskConical, Package, Clock, Check, Plus, 
  ShoppingBag, ChevronRight, ShieldCheck, Tag,
  Building2, Sparkles, Layers, CheckCircle2, Trash2
} from "lucide-react";
import axios from "axios";
import { fetchSymptoms } from "../../services/LabTestApi/symptomsApi";
import { getAllTests } from "../../services/LabTestApi/PopularTest";
import { useCartActions } from "../../hooks/useCartActions";
import { useLocationContext } from "../../context/LocationContext";

export default function SymptomsDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { symptomId: paramId } = useParams();
  const stateSymptom = location.state?.symptom;
  const { address, coords, selectedCity } = useLocationContext();

  const cart = useSelector((state) => state.cart.items || []);
  const { addWithReplaceCheck, removeItem, LoginPopupComponent } = useCartActions();

  const [activeSymptom, setActiveSymptom] = useState(stateSymptom || { id: paramId || 1, name: "Health Condition" });
  const [allSymptoms, setAllSymptoms] = useState([]);
  const [tests, setTests] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activeTab, setActiveTab] = useState("tests"); // 'tests' | 'packages'
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const currentSymptomId = activeSymptom?.id || activeSymptom?.symptomId || paramId || 1;
  const currentSymptomName = activeSymptom?.name || activeSymptom?.symptomName || "Health Concern";

  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return 999;
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) ** 2;
    return 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * R;
  };

  // Load Symptoms, Tests & Packages
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const sourceLat = selectedCity?.latitude ?? selectedCity?.lat ?? coords?.lat;
        const sourceLng = selectedCity?.longitude ?? selectedCity?.lng ?? coords?.lng;

        // 1. All Symptoms for top scroller
        const sympData = await fetchSymptoms();
        if (Array.isArray(sympData)) {
          setAllSymptoms(sympData);
          if (!stateSymptom && paramId) {
            const found = sympData.find(s => Number(s.id) === Number(paramId));
            if (found) setActiveSymptom(found);
          }
        }

        // 2. Fetch Tests specifically for this symptom using TestSearch API
        let rawTests = [];
        try {
          const searchRes = await axios.post("https://admin.medrphalabs.com/api/Lab/TestSearch", {
            name: "",
            labId: 0,
            symptomId: Number(currentSymptomId),
            pageNumber: 1,
            pageSize: 100
          });
          const resData = searchRes.data?.data || searchRes.data?.items || searchRes.data;
          if (Array.isArray(resData) && resData.length > 0) {
            rawTests = resData;
          }
        } catch (e) {
          console.warn("SearchBySymptom fallback:", e);
        }

        // If specific symptom returns empty, fallback to master tests filtered by keyword
        if (rawTests.length === 0) {
          const testRes = await getAllTests(1, 100);
          const allList = Array.isArray(testRes?.data) ? testRes.data : (Array.isArray(testRes) ? testRes : []);
          const keyword = currentSymptomName.toLowerCase().split(' ')[0];
          const matched = allList.filter(t => 
            Number(t.symptomId) === Number(currentSymptomId) || 
            Number(t.symptomsId) === Number(currentSymptomId) ||
            (t.testName || "").toLowerCase().includes(keyword)
          );
          rawTests = matched.length > 0 ? matched : allList.slice(0, 25);
        }

        // 3. DEDUPLICATE TESTS & Location Filter (20km radius)
        const seenIds = new Set();
        const seenNames = new Set();
        const uniqueTests = [];

        for (const t of rawTests) {
          const tId = t.testID || t.testId || t.id;
          const tName = (t.testName || t.name || "").trim().toLowerCase();
          
          if (!tName) continue;

          // Location Filter
          if (sourceLat && sourceLng) {
            const tLat = Number(t.latitude || t.Latitude || t.lat || t.Lat);
            const tLng = Number(t.longitude || t.Longitude || t.Longnitude || t.longnitude || t.lng || t.Lng);
            if (!isNaN(tLat) && !isNaN(tLng) && tLat !== 0 && tLng !== 0) {
              const dist = getDistanceKm(Number(sourceLat), Number(sourceLng), tLat, tLng);
              if (dist > 20) continue;
            }
          }

          // Unique check by ID and normalized Name
          if (tId && seenIds.has(tId)) continue;
          if (seenNames.has(tName)) continue;

          if (tId) seenIds.add(tId);
          seenNames.add(tName);
          uniqueTests.push(t);
        }

        setTests(uniqueTests);

        // 4. Fetch Packages, Location filter & deduplicate
        const pkgRes = await axios.get("https://admin.medrphalabs.com/api/Package?id=0");
        let pkgList = [];
        if (Array.isArray(pkgRes.data)) {
          pkgList = pkgRes.data;
        } else if (Array.isArray(pkgRes.data?.data)) {
          pkgList = pkgRes.data.data;
        }

        const seenPkg = new Set();
        const uniquePackages = [];
        for (const p of pkgList) {
          const pId = p.packageId || p.id;
          const pName = (p.packageName || p.name || "").trim().toLowerCase();
          if (!pName || p.isActive === false) continue;

          // Location Filter
          if (sourceLat && sourceLng) {
            const pLat = Number(p.latitude || p.Latitude || p.lat || p.Lat);
            const pLng = Number(p.longitude || p.Longitude || p.Longnitude || p.longnitude || p.lng || p.Lng);
            if (!isNaN(pLat) && !isNaN(pLng) && pLat !== 0 && pLng !== 0) {
              const dist = getDistanceKm(Number(sourceLat), Number(sourceLng), pLat, pLng);
              if (dist > 20) continue;
            }
          }

          if (pId && seenPkg.has(pId)) continue;
          if (pId) seenPkg.add(pId);
          uniquePackages.push(p);
        }

        setPackages(uniquePackages);

      } catch (err) {
        console.error("Error loading symptom details:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentSymptomId, currentSymptomName, coords?.lat, coords?.lng, selectedCity]);

  const isTestInCart = (testId) => {
    return cart.some((item) => Number(item.productId) === Number(testId) && Number(item.categoryId) === 2);
  };

  const isPackageInCart = (packageId) => {
    return cart.some((item) => Number(item.productId) === Number(packageId) && Number(item.categoryId) === 3);
  };

  const handleTestCartToggle = async (test) => {
    const testId = test.testID || test.testId || test.id;
    if (isTestInCart(testId)) {
      removeItem(testId, 2);
    } else {
      await addWithReplaceCheck({
        productId: testId,
        name: test.testName || test.name,
        price: test.sellingPrice || test.testPrice || test.price || 0,
        discountedPrice: test.discount || 0,
        categoryId: 2,
        storeId: test.labId || 1,
        image: test.testImage || '',
      });
    }
  };

  const handlePackageCartToggle = async (pkg) => {
    const pkgId = pkg.packageId || pkg.id;
    if (isPackageInCart(pkgId)) {
      removeItem(pkgId, 3);
    } else {
      await addWithReplaceCheck({
        productId: pkgId,
        name: pkg.packageName || pkg.name,
        price: pkg.packagePrice || pkg.price || 0,
        discountedPrice: pkg.discountPrice || 0,
        categoryId: 3,
        storeId: pkg.labId || 1,
        image: pkg.image || '',
      });
    }
  };

  const toggleCompare = (testId) => {
    setSelectedForCompare((prev) => 
      prev.includes(testId) 
        ? prev.filter((id) => id !== testId) 
        : prev.length < 4 ? [...prev, testId] : prev
    );
  };

  // Helper for dynamic symptom image URL
  const getSymptomImg = (s) => {
    const imgName = s?.symptoms_Image || s?.symptomsImage || s?.image;
    if (imgName) {
      return imgName.startsWith("http") ? imgName : `https://admin.medrphalabs.com/SymptomsImage/${imgName}`;
    }
    return null;
  };

  // Helper for dynamic package image URL
  const getPackageImgUrl = (pkg) => {
    const raw = pkg.dynamicImageUrl || pkg.image || pkg.packageImage || pkg.PackageImage || pkg.package_image || pkg.packageImageUrl || pkg.imageUrl || pkg.Image || pkg.imagePath;
    if (raw && typeof raw === "string" && raw.trim()) {
      const clean = raw.trim();
      if (clean.startsWith("http")) return clean;
      const normalized = clean.replace(/^\/+/, '');
      if (normalized.startsWith("PackageImage/") || normalized.startsWith("LabImage/") || normalized.startsWith("TestImage/")) {
        return `https://admin.medrphalabs.com/${normalized}`;
      }
      return `https://admin.medrphalabs.com/PackageImage/${normalized}`;
    }
    return null;
  };

  const displayTests = tests.filter(t => 
    (t.testName || t.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const displayPackages = packages.filter(p => 
    (p.packageName || p.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalCartPrice = cart.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.qty) || 1), 0);
  const activeSymptomImg = getSymptomImg(activeSymptom);

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-32 text-slate-800 font-sans">
      {LoginPopupComponent}

      {/* ================= 1. FULL WIDTH PAGE HEADER (Matches LabList.js) ================= */}
      <div className="bg-gradient-to-r from-red-600 via-rose-600 to-red-700 text-white py-6 px-4 sm:px-6 lg:px-10 shadow-md">
        <div className="max-w-[1720px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            
            <div className="flex items-center gap-4">
              {/* Back Button */}
              <button 
                onClick={() => navigate(-1)} 
                className="w-10 h-10 rounded-2xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors text-white shrink-0 shadow-xs"
                title="Go Back"
              >
                <ArrowLeft size={20} />
              </button>

              {/* Symptom Avatar (60x60) */}
              <div className="w-[60px] h-[60px] rounded-2xl bg-white shadow-md flex items-center justify-center shrink-0 p-2 overflow-hidden border-2 border-white/40">
                {activeSymptomImg ? (
                  <img 
                    src={activeSymptomImg} 
                    alt={currentSymptomName} 
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <HeartPulse size={30} className="text-[#E53935]" />
                )}
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <span className="bg-white/20 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
                    <HeartPulse size={11} className="text-amber-300" />
                    <span>Health Concern</span>
                  </span>
                  <span className="text-red-100 text-xs font-semibold">
                    {displayTests.length} Unique Tests • {displayPackages.length} Packages
                  </span>
                </div>

                <h1 className="text-2xl sm:text-3xl font-black mt-0.5 tracking-tight text-white">
                  {currentSymptomName} Tests & Packages
                </h1>
                
                <p className="text-xs sm:text-sm text-red-100 mt-0.5 flex items-center gap-1.5 font-medium">
                  <ShieldCheck size={14} className="text-emerald-300 shrink-0" />
                  <span>100% NABL Accredited Labs • Free Home Sample Collection</span>
                </p>
              </div>
            </div>

            {/* Header Search Box */}
            <div className="w-full md:w-96">
              <div className="h-11 bg-white rounded-2xl px-4 flex items-center justify-between shadow-md text-slate-800">
                <div className="flex items-center gap-2.5 text-slate-400 w-full">
                  <Search size={18} className="text-red-600 shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={`Search in ${currentSymptomName}...`}
                    className="w-full bg-transparent focus:outline-none text-xs sm:text-sm text-slate-800 placeholder-slate-400 font-medium"
                  />
                </div>
                {searchQuery ? (
                  <button onClick={() => setSearchQuery("")} className="text-slate-400 p-1 hover:text-slate-600">
                    <X size={16} />
                  </button>
                ) : (
                  <Mic size={17} className="text-red-600" />
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="w-full max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 mt-6 space-y-6">
        
        {/* ================= 2. GRADIENT HEALTH CONCERNS SCROLLER (Matches LabList.js) ================= */}
        <div className="bg-white border border-slate-200/90 rounded-3xl p-5 shadow-2xs">
          <div className="flex items-center justify-between mb-3.5">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-5 bg-gradient-to-b from-red-500 to-rose-600 rounded-full" />
              <h3 className="text-sm sm:text-base font-extrabold text-slate-900 leading-tight">
                Switch Health Concern
              </h3>
            </div>
            <span className="text-xs text-slate-400 font-medium">Click to change concern</span>
          </div>

          <div className="flex items-center gap-3 overflow-x-auto no-scrollbar py-1">
            {allSymptoms.map((s, idx) => {
              const sId = s.id || idx;
              const sName = s.name || s.symptomName || "Condition";
              const sImg = getSymptomImg(s);
              const isActive = Number(sId) === Number(currentSymptomId);

              return (
                <button
                  key={sId}
                  onClick={() => {
                    setActiveSymptom(s);
                    setSearchQuery("");
                  }}
                  className={`group flex items-center gap-2.5 px-4 py-2 rounded-2xl border text-xs sm:text-sm font-bold transition-all shrink-0 ${
                    isActive 
                      ? "bg-red-600 text-white border-red-600 shadow-md shadow-red-200 scale-102" 
                      : "bg-slate-50 hover:bg-red-50/50 text-slate-700 hover:text-red-600 border-slate-200 hover:border-red-200"
                  }`}
                >
                  <div className={`w-7 h-7 rounded-xl flex items-center justify-center p-1 overflow-hidden shrink-0 transition-transform group-hover:scale-110 ${
                    isActive ? 'bg-white' : 'bg-white border border-slate-200'
                  }`}>
                    {sImg ? (
                      <img src={sImg} alt={sName} className="w-full h-full object-contain" />
                    ) : (
                      <HeartPulse size={14} className="text-red-600" />
                    )}
                  </div>
                  <span>{sName}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ================= 3. TAB CONTROLS ================= */}
        <div className="flex items-center justify-between flex-wrap gap-4 border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2 bg-slate-200/80 p-1.5 rounded-2xl max-w-md">
            <button
              onClick={() => setActiveTab("tests")}
              className={`px-5 py-2.5 text-xs sm:text-sm font-extrabold rounded-xl transition-all flex items-center gap-2 ${
                activeTab === "tests"
                  ? "bg-white text-[#E53935] shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <FlaskConical size={15} />
              <span>Recommended Tests ({displayTests.length})</span>
            </button>
            <button
              onClick={() => setActiveTab("packages")}
              className={`px-5 py-2.5 text-xs sm:text-sm font-extrabold rounded-xl transition-all flex items-center gap-2 ${
                activeTab === "packages"
                  ? "bg-white text-[#E53935] shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Package size={15} />
              <span>Health Packages ({displayPackages.length})</span>
            </button>
          </div>

          <div className="text-xs font-bold text-slate-500 flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-200/60 px-3.5 py-1.5 rounded-xl">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Free Home Sample Pickup Available</span>
          </div>
        </div>

        {/* ================= 4. FULL SCREEN TESTS CONTENT ================= */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-4 border border-[#E8F0FE] animate-pulse flex items-center gap-3.5">
                <div className="w-[68px] h-[68px] bg-slate-200 rounded-xl shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-1/2" />
                  <div className="h-3 bg-slate-100 rounded w-1/3" />
                  <div className="h-4 bg-slate-100 rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : activeTab === "tests" ? (
          displayTests.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-200 max-w-md mx-auto my-6">
              <FlaskConical size={44} className="text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-bold text-slate-800">No Tests Found</h3>
              <p className="text-xs text-slate-500 mt-1">No diagnostic tests matching "{searchQuery}"</p>
              <button
                onClick={() => setSearchQuery("")}
                className="mt-4 bg-[#E53935] text-white font-bold text-xs px-5 py-2.5 rounded-xl hover:bg-red-700 shadow-xs"
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
              {displayTests.map((test, idx) => {
                const testId = test.testID || test.testId || test.id;
                const inCart = isTestInCart(testId);
                const testTitle = test.testName || test.name || "Diagnostic Blood Test";
                const sellingPrice = test.sellingPrice || test.testPrice || test.price || 399;
                const originalPrice = test.discount ? sellingPrice + Number(test.discount) : Math.round(sellingPrice * 1.4);
                const discount = originalPrice > sellingPrice ? Math.round(((originalPrice - sellingPrice) / originalPrice) * 100) : 30;
                const testImg = test.testImage || test.TestImage || test.image;
                const testImgUrl = testImg && typeof testImg === "string" && testImg.trim() ? (
                  testImg.startsWith("http") ? testImg :
                  testImg.startsWith("TestMasterImage/") || testImg.startsWith("TestImage/") || testImg.startsWith("LabImage/")
                    ? `https://admin.medrphalabs.com/${testImg.trim()}`
                    : `https://admin.medrphalabs.com/TestImage/${testImg.trim()}`
                ) : null;
                const isCompared = selectedForCompare.includes(testId);
                const labTitle = test.lab?.lab_name || test.labName || "Certified Partner Lab";

                return (
                  <div
                    key={testId || idx}
                    onClick={() => navigate(`/TestDetail/${testId}`)}
                    className="bg-white rounded-2xl border border-slate-200/90 hover:border-red-400 p-3.5 shadow-2xs hover:shadow-lg transition-all flex flex-col justify-between cursor-pointer group relative overflow-hidden"
                  >
                    <div>
                      {/* Large Top Image Showcase (Matches Packages Card) */}
                      <div className="h-36 sm:h-40 w-full rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center p-2.5 relative overflow-hidden">
                        
                        {/* Discount Badge */}
                        <div className="absolute top-2 left-2 flex items-center gap-1 bg-[#E53935] text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded shadow-2xs z-10">
                          <span>{discount}% OFF</span>
                        </div>

                        {/* Compare Toggle Checkbox */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCompare(testId);
                          }}
                          className={`absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded transition-all border flex items-center gap-1 z-10 ${
                            isCompared 
                              ? "bg-indigo-600 text-white border-indigo-600 shadow-xs" 
                              : "bg-white/90 backdrop-blur-xs text-slate-700 border-slate-200 hover:border-indigo-300"
                          }`}
                          title="Compare Test"
                        >
                          <Layers size={10} />
                          <span>{isCompared ? "Added" : "Compare"}</span>
                        </button>

                        {/* Fast Report Badge */}
                        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-white/95 backdrop-blur-xs text-blue-800 text-[10px] font-extrabold px-2 py-0.5 rounded shadow-xs border border-blue-100 z-10">
                          <Clock size={11} className="text-blue-600" />
                          <span>24 hrs Report</span>
                        </div>

                        {/* Live Image or Name Banner Fallback */}
                        {testImgUrl ? (
                          <img 
                            src={testImgUrl} 
                            alt={testTitle} 
                            className="max-h-full max-w-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.parentElement.querySelector('.name-fallback');
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        ) : null}

                        {/* Product Name Fallback when image missing */}
                        <div 
                          className={`name-fallback w-full h-full flex-col items-center justify-center text-center p-3 bg-gradient-to-br from-red-50 to-rose-100/60 rounded-lg ${testImgUrl ? 'hidden' : 'flex'}`}
                        >
                          <div className="w-12 h-12 rounded-full bg-[#E53935] text-white flex items-center justify-center mb-2 shadow-md">
                            <FlaskConical size={22} />
                          </div>
                          <span className="text-xs font-black text-slate-800 line-clamp-2 px-1 leading-tight text-center">
                            {testTitle}
                          </span>
                        </div>
                      </div>

                      {/* Test Information */}
                      <div className="mt-3">
                        <div className="inline-flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.2 rounded mb-1 truncate max-w-full">
                          <Building2 size={10} className="shrink-0" />
                          <span className="truncate">{labTitle}</span>
                        </div>

                        <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 group-hover:text-[#E53935] transition-colors line-clamp-2 leading-snug">
                          {testTitle}
                        </h4>

                        <div className="mt-2 flex items-baseline gap-2">
                          <span className="text-sm font-black text-slate-900">₹{sellingPrice}</span>
                          <span className="text-xs text-slate-400 line-through">₹{originalPrice}</span>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Cart Action Button */}
                    <div className="mt-3 pt-2.5 border-t border-slate-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTestCartToggle(test);
                        }}
                        className={`w-full py-2 px-3 rounded-xl text-xs font-bold transition-all border flex items-center justify-center gap-1.5 shadow-2xs ${
                          inCart 
                            ? "bg-[#E53935] hover:bg-red-700 text-white border-[#E53935] shadow-sm" 
                            : "bg-white text-[#E53935] border-[#E53935] hover:bg-red-50"
                        }`}
                      >
                        {inCart ? (
                          <>
                            <Trash2 size={13} />
                            <span>REMOVE</span>
                          </>
                        ) : (
                          <>
                            <Plus size={13} />
                            <span>ADD TO CART</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* ================= 5. PACKAGES CONTENT (Matches Flutter Package Cards) ================= */
          displayPackages.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-200 max-w-md mx-auto my-6">
              <Package size={44} className="text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-bold text-slate-800">No Health Packages Found</h3>
              <p className="text-xs text-slate-500 mt-1">No packages available matching "{searchQuery}"</p>
              <button
                onClick={() => setSearchQuery("")}
                className="mt-4 bg-[#E53935] text-white font-bold text-xs px-5 py-2.5 rounded-xl hover:bg-red-700 shadow-xs"
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
              {displayPackages.map((pkg, idx) => {
                const pkgId = pkg.packageId || pkg.id;
                const inCart = isPackageInCart(pkgId);
                const pkgTitle = pkg.packageName || pkg.name || "Full Body Health Checkup";
                const testCount = Array.isArray(pkg.details) ? pkg.details.length : 12;
                const sellingPrice = pkg.packagePrice || pkg.price || 799;
                const mrp = pkg.totalPrice || (sellingPrice + (pkg.discountPrice || 800));
                const discount = mrp > sellingPrice ? Math.round(((mrp - sellingPrice) / mrp) * 100) : 35;
                const pkgImgUrl = getPackageImgUrl(pkg);

                return (
                  <div
                    key={pkgId || idx}
                    onClick={() => navigate(`/package-details/${pkgId}`)}
                    className="bg-white rounded-2xl border border-slate-200/90 hover:border-red-400 p-3.5 shadow-2xs hover:shadow-lg transition-all flex flex-col justify-between cursor-pointer group relative overflow-hidden"
                  >
                    <div>
                      {/* Image Box */}
                      <div className="h-36 sm:h-40 w-full rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center p-2.5 relative overflow-hidden">
                        
                        {/* Discount Badge */}
                        <div className="absolute top-2 left-2 flex items-center gap-1 bg-[#E53935] text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded shadow-2xs z-10">
                          <span>{discount}% OFF</span>
                        </div>

                        {/* Test Count Badge */}
                        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-white/95 backdrop-blur-xs text-blue-800 text-[10px] font-extrabold px-2 py-0.5 rounded shadow-xs border border-blue-100 z-10">
                          <FlaskConical size={11} className="text-blue-600" />
                          <span>{testCount > 0 ? `${testCount} Tests` : "Full Profile"}</span>
                        </div>

                        {/* Live Image or Name Banner Fallback */}
                        {pkgImgUrl ? (
                          <img 
                            src={pkgImgUrl} 
                            alt={pkgTitle} 
                            className="max-h-full max-w-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.parentElement.querySelector('.pkg-fallback');
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        ) : null}

                        {/* Fallback container with product name */}
                        <div 
                          className={`pkg-fallback w-full h-full flex-col items-center justify-center text-center p-3 bg-gradient-to-br from-red-50 to-rose-100/60 rounded-lg ${pkgImgUrl ? 'hidden' : 'flex'}`}
                        >
                          <div className="w-12 h-12 rounded-full bg-[#E53935] text-white flex items-center justify-center mb-2 shadow-md">
                            <Package size={22} />
                          </div>
                          <span className="text-xs font-black text-slate-800 line-clamp-2 px-1 leading-tight text-center">
                            {pkgTitle}
                          </span>
                        </div>
                      </div>

                      {/* Package Info */}
                      <div className="mt-3">
                        <div className="inline-flex items-center gap-1 text-[10px] font-bold text-[#E53935] bg-red-50 px-1.5 py-0.2 rounded mb-1 truncate max-w-full">
                          <Building2 size={10} className="shrink-0" />
                          <span>Certified Partner Lab</span>
                        </div>

                        <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 group-hover:text-[#E53935] transition-colors line-clamp-2 leading-snug">
                          {pkgTitle}
                        </h4>

                        <div className="mt-2 flex items-baseline gap-2">
                          <span className="text-sm font-black text-slate-900">₹{sellingPrice}</span>
                          <span className="text-xs text-slate-400 line-through">₹{mrp}</span>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Cart Action Button */}
                    <div className="mt-3 pt-2.5 border-t border-slate-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePackageCartToggle(pkg);
                        }}
                        className={`w-full py-2 px-3 rounded-xl text-xs font-bold transition-all border flex items-center justify-center gap-1.5 shadow-2xs ${
                          inCart 
                            ? "bg-[#E53935] hover:bg-red-700 text-white border-[#E53935] shadow-sm" 
                            : "bg-white text-[#E53935] border-[#E53935] hover:bg-red-50"
                        }`}
                      >
                        {inCart ? (
                          <>
                            <Trash2 size={13} />
                            <span>REMOVE</span>
                          </>
                        ) : (
                          <>
                            <ShoppingBag size={13} />
                            <span>ADD TO CART</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

      </div>

      {/* ================= 6. FLUTTER COMPARE FLOATING BAR ================= */}
      {selectedForCompare.length >= 2 && (
        <div className="fixed bottom-20 left-4 right-4 max-w-md mx-auto z-40 bg-[#1E1B4B] text-white p-3.5 rounded-2xl shadow-2xl flex items-center justify-between gap-3 border border-indigo-500/30">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2 overflow-hidden">
              {selectedForCompare.map((id, i) => (
                <div key={id} className="w-7 h-7 rounded-full bg-indigo-500 border-2 border-[#1E1B4B] flex items-center justify-center text-[10px] font-bold">
                  {i + 1}
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-bold text-white leading-tight">
                {selectedForCompare.length} Tests Selected
              </p>
              <p className="text-[10px] text-indigo-200">Ready to compare</p>
            </div>
          </div>

          <button
            onClick={() => navigate('/test-comparison', { state: { testIds: selectedForCompare } })}
            className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white font-bold text-xs px-4 py-2 rounded-xl shadow-md flex items-center gap-1"
          >
            <span>Compare Now</span>
            <ChevronRight size={13} />
          </button>
        </div>
      )}

      {/* ================= 7. STICKY BOTTOM CART BAR ================= */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 p-3 sm:p-4 shadow-2xl">
          <div className="max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 text-[#E53935] flex items-center justify-center font-black">
                <ShoppingBag size={20} />
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500">
                  {cart.length} {cart.length === 1 ? "Item" : "Items"} in Cart
                </span>
                <p className="text-base sm:text-lg font-black text-slate-900">
                  Total: ₹{totalCartPrice}
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate("/cart")}
              className="bg-[#E53935] hover:bg-red-700 text-white font-black text-xs sm:text-sm px-6 py-2.5 rounded-xl shadow-md transition-all flex items-center gap-1.5"
            >
              <span>View Cart & Checkout</span>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
